import { z } from "zod";
import { specialtySchema } from "./clinician.js";
/**
 * Staff administration.
 *
 * Creating a specialist or a coordinator means granting a clinical or
 * cross-patient role, so this is the most privileged surface in the product
 * and the only one that can change what another account is allowed to do.
 *
 * Three constraints are structural rather than advisory, because they are what
 * keep the blast radius bounded:
 *
 *   1. Only `clinician` and `admin` can ever be granted here. `patient` is
 *      excluded deliberately - patients arrive by self-signup, and an operator
 *      creating them by hand would be creating an account somebody else is
 *      supposed to own.
 *
 *   2. The role is chosen from a closed set, never passed through as a free
 *      string. A group name that reached Cognito unvalidated would be a way to
 *      invent authorization out of thin air.
 *
 *   3. Nobody may change their own roles. That blocks the obvious escalation,
 *      and equally the accident of a sole coordinator removing their own
 *      access and locking the organisation out.
 */
/** Roles an administrator may grant. Note the absence of `patient`. */
export const GRANTABLE_ROLES = ["clinician", "admin"];
export const grantableRoleSchema = z.enum(GRANTABLE_ROLES);
export function isGrantableRole(value) {
    return GRANTABLE_ROLES.includes(value);
}
export function roleLabel(role) {
    switch (role) {
        case "clinician":
            return "Specialist";
        case "admin":
            return "Care coordinator";
        case "patient":
            return "Patient";
    }
}
/**
 * The clinical profile that accompanies a specialist.
 *
 * A Cognito account alone makes somebody able to sign in but not able to be
 * booked - patients browse profiles, and a specialist without one is invisible.
 * Creating the two together is what stops a half-made specialist existing.
 */
export const clinicianProfileInputSchema = z.object({
    specialty: specialtySchema,
    credentials: z.string().trim().min(2).max(200),
    bio: z.string().trim().min(40, "Patients choose a specialist on this. A sentence is not enough.").max(2000),
    hospital: z.string().trim().min(2).max(200),
    country: z
        .string()
        .trim()
        .length(2, "Two-letter ISO country code, e.g. IN or GB.")
        .transform((v) => v.toUpperCase()),
    years_experience: z.number().int().min(0).max(70),
    languages: z
        .array(z.string().trim().min(2).max(8))
        .min(1, "At least one language.")
        .max(10),
});
export const createStaffSchema = z
    .object({
    email: z.string().trim().email().max(256),
    name: z.string().trim().min(2).max(128),
    role: grantableRoleSchema,
    /** IANA zone. Drives every time this person is ever shown. */
    timezone: z.string().trim().min(1).max(64),
    /** Required for a specialist, meaningless for a coordinator. */
    profile: clinicianProfileInputSchema.optional(),
})
    .superRefine((value, ctx) => {
    if (value.role === "clinician" && value.profile === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["profile"],
            message: "A specialist needs a clinical profile before patients can book them.",
        });
    }
    if (value.role === "admin" && value.profile !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["profile"],
            message: "A coordinator does not have a clinical profile.",
        });
    }
});
export const setStaffEnabledSchema = z.object({
    enabled: z.boolean(),
    reason: z.string().trim().max(500).optional(),
});
//# sourceMappingURL=administration.js.map